import type { Ride } from '../domain/ride';
import { calculateRideMetrics } from '../domain/ride';
import { filmDuration } from '../domain/film-timing';
import { cinematicTimeline } from '../domain/cinematic-timeline';
import { resolveExportTiming } from '../../../renderer/src/export-range';
import { emptyCinematicSequence } from '../../../renderer/src/cinematic-sequence';
import { studioDraftSchema, videoPreferenceSchema, type StudioDraft } from './studio-draft';
import { recoveryIssue, recoveryLabel, type RecoveryIssue } from './draft-recovery-description';
import { recoverAppearances } from './recover-appearances';

export const RECOVERY_MESSAGE = 'Submission is paused until you review the changed settings.';
export interface RecoveredDraft { draft: StudioDraft; issues: RecoveryIssue[] }
export interface DraftValidationIssue { message: string; target: 'ride' | 'composition' | 'camera' }

function targetFor(field: string): DraftValidationIssue['target'] {
  if (['title', 'timing', 'preset', 'exportRange', 'progress'].includes(field)) return 'ride';
  if (['camera', 'distance', 'cameraGimbal', 'cameraAngle', 'cinematicSequence', 'cinematicEnabled', 'depthOfField'].includes(field)) return 'camera';
  return 'composition';
}

function draftDuration(ride: Ride, draft: StudioDraft) {
  return filmDuration((ride.metrics ?? calculateRideMetrics(ride.points)).distanceKilometers, draft.timing);
}

export function validateDraftTimeline(ride: Ride, draft: StudioDraft) {
  cinematicTimeline(ride.sourcePoints ?? ride.points, draft.cinematicSequence, draftDuration(ride, draft));
}

export function validateDraftRange(ride: Ride, draft: StudioDraft) {
  return resolveExportTiming({ durationSeconds: draftDuration(ride, draft),
    exportRange: draft.exportRange ? { ...draft.exportRange,
      ...(!draft.keepSourcePace && draft.timing.mode === 'duration' ? { durationSeconds: draft.timing.targetSeconds } : {}) } : undefined });
}

/** Validation is shared by recovery and explicit acceptance. */
export function draftValidationIssue(ride: Ride, draft: StudioDraft): DraftValidationIssue | null {
  const parsed = studioDraftSchema.safeParse(draft);
  if (!parsed.success) {
    const field = String(parsed.error.issues[0]?.path[0] ?? '');
    return { message: `Check ${recoveryLabel(field)} in ${targetFor(field) === 'ride' ? 'Trace & format' : targetFor(field) === 'camera' ? 'Camera & motion' : 'Composition'}. Its current value cannot be saved.`, target: targetFor(field) };
  }
  try { validateDraftTimeline(ride, draft); }
  catch { return { message: 'Check Camera keyframes in Camera & motion. Some keys do not match this trace or share the same film position.', target: 'camera' }; }
  try { validateDraftRange(ride, draft); }
  catch { return { message: 'Check Selected footage in Trace & format. IN and OUT must be ordered frames within the film.', target: 'ride' }; }
  return null;
}

/** Recover supported fields without writing over the saved composition. */
export function recoverDraft(raw: string, defaults: StudioDraft, reusable: boolean): RecoveredDraft {
  const envelope = JSON.parse(raw) as { version?: number; draft?: Record<string, unknown> } | null;
  if (!envelope || envelope.version !== 1 || !envelope.draft || typeof envelope.draft !== 'object' || Array.isArray(envelope.draft)) throw new Error('unsupported-format');
  if (!reusable && envelope.draft.sourceKey !== defaults.sourceKey) throw new Error('different-recording');
  const recovered: Record<string, unknown> = { ...defaults };
  if (!reusable && !('cameraStyleVersion' in envelope.draft)) recovered.cameraStyleVersion = 3;
  const issues: RecoveryIssue[] = [];
  for (const [key, schema] of Object.entries(studioDraftSchema.shape)) {
    if (key === 'sourceKey' || (reusable && ['title', 'placeTitle', 'progress', 'cinematicEnabled', 'cinematicSequence', 'exportRange', 'cameraStyleVersion'].includes(key))) continue;
    if (!(key in envelope.draft)) continue;
    const candidate = envelope.draft[key];
    if (key === 'appearances') {
      const result = recoverAppearances(candidate, defaults.appearances);
      recovered.appearances = result.appearances; issues.push(...result.issues); continue;
    }
    const parsed = schema.safeParse(candidate);
    if (parsed.success) recovered[key] = parsed.data;
    else issues.push(recoveryIssue(key, candidate, defaults[key as keyof StudioDraft]));
  }
  return { draft: studioDraftSchema.parse(recovered), issues };
}

export function recoverDraftTimeline(ride: Ride, result: RecoveredDraft): RecoveredDraft {
  const draft = { ...result.draft }; const issues = [...result.issues];
  try { validateDraftTimeline(ride, draft); }
  catch {
    const replacement = emptyCinematicSequence();
    issues.push(recoveryIssue('cinematicSequence', draft.cinematicSequence, replacement,
      'These camera keyframes do not match this trace or share the same film position. The camera animation will be reset.'));
    draft.cinematicSequence = replacement;
  }
  try { validateDraftRange(ride, draft); }
  catch {
    issues.push(recoveryIssue('exportRange', draft.exportRange, null, 'The saved IN/OUT points are outside the film or are in the wrong order. The full film is selected instead.'));
    draft.exportRange = null;
  }
  return { draft, issues };
}

export function legacyRecoveryIssues(): RecoveryIssue[] {
  const issues: RecoveryIssue[] = [];
  for (const key of ['ride-trace.video-presentation.v1', 'ride-trace.video-presentation.v2']) {
    const raw = localStorage.getItem(key); if (!raw) continue;
    let value: unknown;
    try { value = JSON.parse(raw); } catch { value = null; }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      issues.push({ field: key, label: 'Earlier video layouts', reason: 'These earlier layout settings cannot be read. Default layouts are used instead.',
        savedValue: 'Unavailable: saved layout data cannot be read', replacementValue: 'Default video layouts', removed: '' }); continue;
    }
    const stored = value as { presets?: Record<string, unknown>; placements?: Record<string, unknown> };
    for (const [identity, candidate] of Object.entries({ ...stored.placements, ...stored.presets })) {
      if (!videoPreferenceSchema.safeParse(candidate).success) issues.push(recoveryIssue(`appearances.${identity}`, candidate, undefined,
        'This earlier layout is not supported. A default layout is used instead.'));
    }
  }
  return issues;
}
